import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import ProgramNavDropdown from './component'
import {
  HOVER_CLOSE_DELAY_MS,
  HOVER_OPEN_DELAY_MS,
  HOVER_SWITCH_DELAY_MS,
  HOVER_ZONE_PADDING_PX,
  ITEM_KINDS,
  POINTER_AIM_STALE_MS,
  POINTER_TRAIL_LENGTH,
} from './constants'
import {
  claimActiveDropdown,
  createPanelId,
  isAnotherDropdownOpen,
  isDesktopViewport,
  isHeadingIntoRect,
  isModifiedClick,
  isPointerHeadingIntoAnotherDropdown,
  isPointInRect,
  releaseActiveDropdown,
} from './helpers'

const useIsDesktopViewport = () => {
  const [isDesktop, setIsDesktop] = useState(isDesktopViewport)

  useEffect(() => {
    const updateIsDesktop = () => setIsDesktop(isDesktopViewport())
    window.addEventListener('resize', updateIsDesktop)
    return () => window.removeEventListener('resize', updateIsDesktop)
  }, [])

  return isDesktop
}

/**
 * Desktop only: the menu opens on hover or keyboard focus and the tab click
 * navigates. Tablet and mobile widths render just the tab.
 *
 * An open menu closes based on where the pointer is, not on DOM mouseleave,
 * so page content overlapping the panel or small overshoots past its edges
 * never close it.
 */
const ProgramNavDropdownContainer = ({
  children,
  isActive,
  items,
  label,
  onNavigate,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [panelId] = useState(createPanelId)
  const wrapperRef = useRef(null)
  const tabSlotRef = useRef(null)
  const positionerRef = useRef(null)
  const isPointerPressedRef = useRef(false)
  const isKeyboardFocusRef = useRef(false)
  const pointerTrailRef = useRef([])
  const openTimeoutRef = useRef(null)
  const closeTimeoutRef = useRef(null)
  const isDesktop = useIsDesktopViewport()
  const hasLinks = !!items?.some(item => item.kind === ITEM_KINDS.link)
  const isEnabled = hasLinks && isDesktop

  const clearOpenTimer = () => {
    clearTimeout(openTimeoutRef.current)
    openTimeoutRef.current = null
  }

  const clearCloseTimer = () => {
    clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = null
  }

  const close = useCallback(() => {
    clearOpenTimer()
    clearCloseTimer()
    releaseActiveDropdown(close)
    setIsOpen(false)
  }, [])

  const isPointerHeadingInside = useCallback(() => {
    const trail = pointerTrailRef.current
    const latest = trail[trail.length - 1]
    if (!latest || Date.now() - latest.time > POINTER_AIM_STALE_MS) {
      return false
    }

    return isHeadingIntoRect({
      from: trail[0],
      to: latest,
      rect: positionerRef.current?.getBoundingClientRect(),
      padding: HOVER_ZONE_PADDING_PX,
    })
  }, [])

  const open = () => {
    clearOpenTimer()
    clearCloseTimer()
    claimActiveDropdown({ close, isPointerHeadingInside })
    setIsOpen(true)
  }

  const closeUnlessKeyboardFocused = () => {
    const hasFocusInside = wrapperRef.current?.contains(document.activeElement)
    if (hasFocusInside && isKeyboardFocusRef.current) return

    close()
  }

  const scheduleClose = () => {
    clearCloseTimer()
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null
      closeUnlessKeyboardFocused()
    }, HOVER_CLOSE_DELAY_MS)
  }

  useEffect(
    () => () => {
      clearOpenTimer()
      clearCloseTimer()
      releaseActiveDropdown(close)
    },
    [],
  )

  useEffect(() => {
    if (!isEnabled) {
      close()
    }
  }, [isEnabled])

  useEffect(() => {
    onOpenChange?.(isOpen)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const closeOnOutsidePress = event => {
      if (!wrapperRef.current?.contains(event.target)) {
        close()
      }
    }
    document.addEventListener('mousedown', closeOnOutsidePress)
    document.addEventListener('touchstart', closeOnOutsidePress)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePress)
      document.removeEventListener('touchstart', closeOnOutsidePress)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const isPointInHoverZone = point =>
      [wrapperRef.current, positionerRef.current].some(element =>
        isPointInRect({
          point,
          rect: element?.getBoundingClientRect(),
          padding: HOVER_ZONE_PADDING_PX,
        }),
      )

    const trackPointer = event => {
      const point = { x: event.clientX, y: event.clientY, time: Date.now() }
      pointerTrailRef.current = [...pointerTrailRef.current, point].slice(
        -POINTER_TRAIL_LENGTH,
      )

      if (isPointInHoverZone(point)) {
        clearCloseTimer()
      } else if (isPointerHeadingInside() || !closeTimeoutRef.current) {
        scheduleClose()
      }
    }

    // Scrolling or resizing moves the menu under a still pointer without a
    // mousemove, so recheck the last pointer position against the new layout.
    const recheckLastPointer = () => {
      const trail = pointerTrailRef.current
      const lastPoint = trail[trail.length - 1]
      if (!lastPoint) return

      if (isPointInHoverZone(lastPoint)) {
        clearCloseTimer()
      } else if (!closeTimeoutRef.current) {
        scheduleClose()
      }
    }

    pointerTrailRef.current = []
    document.addEventListener('mousemove', trackPointer)
    window.addEventListener('scroll', recheckLastPointer, true)
    window.addEventListener('resize', recheckLastPointer)
    return () => {
      document.removeEventListener('mousemove', trackPointer)
      window.removeEventListener('scroll', recheckLastPointer, true)
      window.removeEventListener('resize', recheckLastPointer)
    }
  }, [isOpen])

  if (!isEnabled) {
    return children ?? null
  }

  const scheduleOpen = delay => {
    clearOpenTimer()
    openTimeoutRef.current = setTimeout(() => {
      if (isPointerHeadingIntoAnotherDropdown(close)) {
        scheduleOpen(HOVER_OPEN_DELAY_MS)
        return
      }
      open()
    }, delay)
  }

  const handleMouseEnter = () => {
    clearCloseTimer()
    if (!isOpen) {
      scheduleOpen(
        isAnotherDropdownOpen(close)
          ? HOVER_SWITCH_DELAY_MS
          : HOVER_OPEN_DELAY_MS,
      )
    }
  }

  const handleMouseLeave = () => {
    clearOpenTimer()
  }

  const handleMouseDown = () => {
    isPointerPressedRef.current = true
    document.addEventListener(
      'mouseup',
      () => {
        isPointerPressedRef.current = false
      },
      { once: true },
    )
  }

  const handleFocus = () => {
    isKeyboardFocusRef.current = !isPointerPressedRef.current
    if (!isOpen) {
      open()
    }
  }

  const handleBlur = event => {
    // Pressing a non-focusable part of the panel blurs to the body.
    if (isPointerPressedRef.current) return

    if (!wrapperRef.current?.contains(event.relatedTarget)) {
      close()
    }
  }

  const handleKeyDown = event => {
    if (event.key !== 'Escape' || !isOpen) return

    // Move focus before closing so focus is not left on an unmounted menu link.
    tabSlotRef.current?.querySelector('a, button, [tabindex]')?.focus()
    close()
  }

  const handleItemClick = (event, route) => {
    if (isModifiedClick(event) || typeof onNavigate !== 'function') return

    event.preventDefault()
    close()
    onNavigate(route)
  }

  return (
    <ProgramNavDropdown
      isActive={isActive}
      isOpen={isOpen}
      items={items}
      label={label}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onItemClick={handleItemClick}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      panelId={panelId}
      positionerRef={positionerRef}
      tabSlotRef={tabSlotRef}
      wrapperRef={wrapperRef}
    >
      {children}
    </ProgramNavDropdown>
  )
}

ProgramNavDropdownContainer.propTypes = {
  children: PropTypes.node,
  isActive: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      kind: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      route: PropTypes.string,
      isSelected: PropTypes.bool,
    }),
  ),
  label: PropTypes.string,
  onNavigate: PropTypes.func,
  onOpenChange: PropTypes.func,
}

export default ProgramNavDropdownContainer
